/**
 * SiteDashboardPage (CLOUD) — the selected site at a glance: the same six summary cards its row
 * shows on the all-sites dashboard, and the same page the local product opens on.
 *
 * It exists so "Dashboard" means something in both scopes. All Sites answers "which site needs
 * me"; this answers "how is THIS site" — and an operator who has switched scope to one site
 * shouldn't have to go back to the estate view and hunt for its row to see its numbers.
 *
 * The reads are the same four the all-sites dashboard fans out per node, run under `allSettled`
 * so one denied or timed-out tunnel call leaves its own card empty rather than blanking the page.
 * Host metrics come from the node registry (which NodeContext already polls) rather than a fifth
 * tunnel call — the cloud knows a node's load without asking it.
 *
 * Below them, what that site's AI has seen today — the same dynamic card row the local dashboard
 * draws, from the same shared component and the same single /analytics/summary call, proxied
 * through this site's tunnel. The window is the SITE's midnight, decided on the node, so a site
 * in another timezone reports its own day rather than the operator's.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowPathIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import {
  NodeSummaryCards,
  formatUptime,
  type NodeSummaryData,
  AiAnalyticsCards,
  type AiAnalyticsSummary,
  useActiveModelSurfaces,
  describeApiError,
} from '@trinetra/shared';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getNodeCameras,
  getNodeGroups,
  getNodeModelAssignments,
  getNodeAlerts,
  getNodeAiSummary,
} from '../services/api.service';

/** Value of a fulfilled settled result, or undefined if it rejected. */
const settled = (r: PromiseSettledResult<any>) => (r.status === 'fulfilled' ? r.value : undefined);

/** The node proxy answers with a bare array, `{data}` or `{data, pagination}`. */
function toArray(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

export default function SiteDashboardPage() {
  const { nodes, selectedNodeId } = useNode();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  // Read the node out of `nodes` rather than `selectedNode`: that object is memoised on
  // id/status/name for reference stability, so its `metrics` would be whatever the poll happened
  // to carry when the name last changed — stale by minutes on a page whose job is load.
  const node = useMemo(
    () => nodes.find((n) => n.nodeId === selectedNodeId) || null,
    [nodes, selectedNodeId],
  );
  const isOnline = node?.status === 'online';

  const [data, setData] = useState<NodeSummaryData>({
    cameras: [], groups: [], assignments: {}, alerts: [], loading: true,
  });
  const [failed, setFailed] = useState<string[]>([]);

  const canCameras = hasPermission('cameras.view');
  const canGroups = hasPermission('groups.view');
  const canModels = hasPermission('ai-models.view');
  const canAlerts = hasPermission('alerts.view');
  // One key for the whole analytics row: the node's command-center has no per-user camera scope,
  // so these are site-wide figures for anyone entitled to see AI events at all.
  const canAiEvents = hasPermission('ai_events.view');
  const canAttendance = hasPermission('attendance.view');

  // Per-node registry, so the hook is keyed on the selected site — switching sites refetches.
  const { surfaces } = useActiveModelSurfaces(selectedNodeId);
  const [aiSummary, setAiSummary] = useState<AiAnalyticsSummary | null>(null);

  const load = useCallback(async () => {
    if (!selectedNodeId || !isOnline) {
      setData({ cameras: [], groups: [], assignments: {}, alerts: [], loading: false });
      return;
    }
    const [cams, groups, assignments, alerts] = await Promise.allSettled([
      canCameras ? getNodeCameras(selectedNodeId) : Promise.resolve([]),
      canGroups ? getNodeGroups(selectedNodeId) : Promise.resolve([]),
      canModels ? getNodeModelAssignments(selectedNodeId) : Promise.resolve({}),
      canAlerts ? getNodeAlerts(selectedNodeId, { limit: 5, sort: 'desc' }) : Promise.resolve({ data: [] }),
    ]);

    setData({
      cameras: toArray(settled(cams)),
      groups: toArray(settled(groups)),
      assignments: settled(assignments) || {},
      alerts: toArray(settled(alerts)),
      loading: false,
    });

    // Named, because "no alerts" and "the tunnel didn't answer" look identical on a card.
    setFailed(
      [
        cams.status === 'rejected' && 'cameras',
        groups.status === 'rejected' && 'groups',
        assignments.status === 'rejected' && 'AI models',
        alerts.status === 'rejected' && 'alerts',
      ].filter(Boolean) as string[],
    );
  }, [selectedNodeId, isOnline, canCameras, canGroups, canModels, canAlerts]);

  useEffect(() => { load(); }, [load]);

  /**
   * The analytics row on its own request, for the same reasons the local dashboard splits it:
   * it depends on the model registry (`surfaces`), which resolves later than the six cards, and
   * it is one more tunnel round-trip that must not delay them. An offline site skips it — the
   * summary lives on the node.
   */
  const loadAi = useCallback(async () => {
    if (!selectedNodeId || !isOnline || !canAiEvents || surfaces === null) {
      setAiSummary(null);
      return;
    }
    try {
      setAiSummary(
        await getNodeAiSummary(selectedNodeId, surfaces, {
          alerts: canAlerts,
          attendance: canAttendance,
        }),
      );
    } catch (err) {
      // Keep the last good figures rather than blanking the row on one flapped tunnel call — but
      // say WHY. "Couldn't read AI summary" is the same sentence whether the site is offline, the
      // tunnel timed out, the role lacks ai_events.view, or the command-center rejected the
      // service token, and those have four different fixes.
      const reason = `AI summary (${describeApiError(err)})`;
      setFailed((prev) => (prev.some((f) => f.startsWith('AI summary')) ? prev : [...prev, reason]));
    }
  }, [selectedNodeId, isOnline, canAiEvents, canAlerts, canAttendance, surfaces]);

  useEffect(() => { loadAi(); }, [loadAi]);

  /**
   * camera id → name, for the alert lines in the analytics section. The node's alerts carry a
   * camera id and no name (they are denormalised for query speed), and this page already holds
   * the site's camera list for the cards below.
   */
  const cameraNames = useMemo(
    () => Object.fromEntries((data.cameras || []).map((c: any) => [c.id, c.name]).filter(([, nm]) => nm)),
    [data.cameras],
  );

  // Same cadence as the all-sites dashboard, paused while hidden — each refresh is four tunnel
  // round-trips to a machine that has other work to do.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden) return;
      load();
      loadAi();
    }, 30_000);
    return () => clearInterval(id);
  }, [load, loadAi]);

  /** Shared by both branches below, so the tiles are identical whether or not AI is permitted. */
  const nodeCardProps = {
    data: { ...data, metrics: node?.metrics || {} },
    links: {
      cameras: canCameras ? '/camera-management' : undefined,
      recording: hasPermission('recordings.view') ? '/recordings' : undefined,
      models: hasPermission('ai-models.assign') ? '/model-assignments' : undefined,
      groups: canGroups ? '/group-camera-map' : undefined,
      status: hasPermission('monitoring.view') ? `/node-monitor/${selectedNodeId}` : undefined,
    },
    // Already scoped to this site, so no selectNode dance — unlike the all-sites row.
    onNavigate: navigate,
  };

  return (
    <div className="space-y-4 p-3 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold text-foreground">
            {node?.name || 'Dashboard'}
          </h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {!selectedNodeId
              ? 'Select a site to see its summary.'
              : isOnline
                ? `This site at a glance${node?.metrics?.uptimeSeconds ? ` · up ${formatUptime(node.metrics.uptimeSeconds)}` : ''}`
                : 'This site is offline.'}
          </p>
        </div>
        <button
          onClick={() => { load(); loadAi(); }}
          title="Refresh"
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <ArrowPathIcon className="h-4 w-4" />
        </button>
      </div>

      {failed.length > 0 && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-[12px] text-destructive">
          Couldn't read {failed.join(', ')} from this site — those cards may be empty or out of date.
        </div>
      )}

      {!selectedNodeId ? (
        <div className="rounded-xl border border-border bg-card p-5 text-[13px] text-muted-foreground shadow-card">
          No site selected. Pick one from the site switcher in the title bar.
        </div>
      ) : !isOnline ? (
        <div className="rounded-xl border border-border bg-card p-3 shadow-card">
          <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-3 text-[12px] text-muted-foreground">
            <NoSymbolIcon className="h-4 w-4 flex-none" />
            <span>Offline — cameras, alerts and metrics are unavailable until this site reconnects.</span>
          </div>
        </div>
      ) : (
        /* ONE sheet, ONE grid, no second heading — the same structure the local dashboard uses, so
           an operator moving between the products reads the same page. "This Site" was dropped
           because it named WHICH thing the tiles were about, and the whole page is this site; the
           site's own tiles now continue the analytics grid, which also puts every card on shared
           row lines. AI first: they came to find out what happened here, not to read meters. */
        <div className="rounded-xl border border-border bg-card shadow-card">
          {canAiEvents ? (
            <section>
              {/* No heading at all now — the cards name themselves and the site's name titles the
                  page above. What CANNOT go is the window: the model cards run on the SITE's
                  calendar day, presence on the attendance work date with its own cutoff hour, and
                  both are the site's clock rather than this browser's. An operator in another
                  timezone reading unlabelled figures would take them for their own day. */}
              <div className="flex flex-wrap items-baseline justify-end gap-2 px-3 pt-3">
                <p className="text-[11px] text-muted-foreground">
                  Today, since midnight at this site
                  {aiSummary?.attendance?.work_date
                    ? ` · presence for work day ${aiSummary.attendance.work_date}`
                    : ''}
                </p>
              </div>
              <AiAnalyticsCards
                className="p-3"
                surfaces={surfaces}
                summary={aiSummary}
                latestAlerts={canAlerts ? data.alerts : undefined}
                cameraNames={cameraNames}
                loading={!aiSummary}
                links={{
                  reports: {
                    face: '/reports/face',
                    person: '/reports/person',
                    'person-count': '/reports/person-count',
                    intrusion: '/reports/intrusion',
                    'vehicle-crossing': '/reports/vehicle-crossing',
                    'ppe-violation': '/reports/ppe-violation',
                    theft: '/reports/theft',
                    fall: '/reports/fall',
                    'fire-smoke': '/reports/fire-smoke',
                  },
                  alerts: canAlerts ? '/alerts' : undefined,
                  attendance: canAttendance ? '/attendance/presence' : undefined,
                }}
                onNavigate={navigate}
                /* The site's own tiles, as cells of the SAME grid — cameras and AI models merged
                   into one, then the machine's load. */
                trailing={<NodeSummaryCards bare cards={['cameras-models', 'status']} {...nodeCardProps} />}
              />
            </section>
          ) : (
            /* No AI permission: the site's tiles are all there is, so they get the grid to
               themselves rather than disappearing with the section that used to host them. */
            <NodeSummaryCards className="p-3" cards={['cameras-models', 'status']} {...nodeCardProps} />
          )}
        </div>
      )}
    </div>
  );
}
